import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from 'react-query';

import { deleteRequest, getRequest, postRequest, putRequest } from 'services/apiRequests';
import { APIList, StringifiedUUID } from 'types';
import { TerritoryDataObject } from 'types/tqModelTree';
import type { APIError } from 'utils/errors';

export type AllViewsTerritoryDataObjectsResponse = Record<StringifiedUUID, TerritoryDataObject[]>;

const apiUrl = (modelId: string, dataObjectId?: string) =>
  `/api/v1/tq/hierarchy-models/${modelId}/objects/${dataObjectId ? `${dataObjectId}/` : ''}`;

export type UpdateTerritoryDataObjectRequest =
  | Pick<TerritoryDataObject, 'name'>
  | {
      parent_id: string;
      position: number;
    };

export type CreateTerritoryDataObjectRequest = Pick<TerritoryDataObject, 'name' | 'external_id'> & {
  parent_id: string;
  object_type: 'territory';
  territory_view_id?: string | null;
};

type UpdateTerritoryDataObjectProps = {
  hierarchyModelId: StringifiedUUID;
  dataObjectId: StringifiedUUID;
  payload: UpdateTerritoryDataObjectRequest;
};

type CreateTerritoryDataObjectProps = {
  hierarchyModelId: StringifiedUUID;
  payload: CreateTerritoryDataObjectRequest;
};

type DeleteTerritoryDataObjectRequest = {
  unassign?: boolean;
};

type DeleteTerritoryDataObjectProps = {
  hierarchyModelId: StringifiedUUID;
  dataObjectId: StringifiedUUID;
  payload: DeleteTerritoryDataObjectRequest;
};

const updateTerritoryDataObject =
  (csrfToken: string) =>
  ({ hierarchyModelId, dataObjectId, payload }: UpdateTerritoryDataObjectProps) => {
    return putRequest<UpdateTerritoryDataObjectRequest, TerritoryDataObject>(
      apiUrl(hierarchyModelId, dataObjectId),
      payload,
      csrfToken,
    );
  };

const createTerritoryDataObject =
  (csrfToken: string) =>
  ({ hierarchyModelId, payload }: CreateTerritoryDataObjectProps) => {
    return postRequest<CreateTerritoryDataObjectRequest, TerritoryDataObject>(
      apiUrl(hierarchyModelId),
      payload,
      csrfToken,
    );
  };

const deleteDataObject =
  (csrfToken: string) =>
  ({ hierarchyModelId, dataObjectId, payload }: DeleteTerritoryDataObjectProps) => {
    return deleteRequest<DeleteTerritoryDataObjectRequest, null>(
      apiUrl(hierarchyModelId, dataObjectId),
      payload,
      csrfToken,
    );
  };

type TerritoryDataObjectsParams = {
  limit?: number;
  filterQuery?: string;
  territoryViewId?: StringifiedUUID | null;
};

const getTerritoryDataObjects = (modelId: StringifiedUUID, params: TerritoryDataObjectsParams = {}) => {
  const queryParams = new URLSearchParams();
  if (params.limit) {
    queryParams.set('limit', params.limit.toString());
  }
  if (params.filterQuery) {
    queryParams.set('q', params.filterQuery);
  }
  if (params.territoryViewId) {
    queryParams.set('territory_view_id', params.territoryViewId);
  }
  const queryString = queryParams.toString();

  return getRequest<null, APIList<TerritoryDataObject>>(`${apiUrl(modelId)}${queryString ? `?${queryString}` : ''}`);
};
const getTerritoryDataObjectsQueryKey = (
  modelId: StringifiedUUID,
  filterQuery?: string,
  territoryViewId?: StringifiedUUID | null,
) =>
  ['hierarchy-models', modelId, 'territory-objects', filterQuery ?? '', territoryViewId ?? 'main'].filter(
    (keyPart): keyPart is string => keyPart !== undefined,
  );
const useGetTerritoryDataObjectsQuery = (modelId: StringifiedUUID, params: TerritoryDataObjectsParams = {}) => {
  const QUERY_KEY = getTerritoryDataObjectsQueryKey(modelId, params.filterQuery, params.territoryViewId);
  const queryClient = useQueryClient();

  const query = useQuery<APIList<TerritoryDataObject>, APIError>({
    queryKey: QUERY_KEY,
    queryFn: () => getTerritoryDataObjects(modelId, params),
    enabled: !!modelId,
  });
  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  return { query, invalidateQuery };
};

const getAllViewsTerritoryDataObjects = (modelId: StringifiedUUID) => {
  const queryParams = new URLSearchParams();
  queryParams.set('include_all_views', 'true');
  return getRequest<null, AllViewsTerritoryDataObjectsResponse>(`${apiUrl(modelId)}?${queryParams.toString()}`);
};

const getAllViewsTerritoryDataObjectsQueryKey = (modelId: StringifiedUUID) => [
  'hierarchy-models',
  modelId,
  'territory-objects',
  'all-views',
];

const useGetAllViewsTerritoryDataObjectsQuery = (
  modelId: StringifiedUUID | undefined,
  options: Omit<UseQueryOptions<AllViewsTerritoryDataObjectsResponse, APIError>, 'queryKey' | 'queryFn'> = {},
) => {
  const QUERY_KEY = getAllViewsTerritoryDataObjectsQueryKey(modelId ?? '');
  const queryClient = useQueryClient();

  const query = useQuery<AllViewsTerritoryDataObjectsResponse, APIError>({
    enabled: !!modelId,
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => getAllViewsTerritoryDataObjects(modelId as StringifiedUUID),
  });
  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  return { query, invalidateQuery };
};

const useCreateTerritoryDataObjectMutation = (
  csrfToken: string,
  options: UseMutationOptions<TerritoryDataObject, APIError, CreateTerritoryDataObjectProps> = {},
) => {
  const queryClient = useQueryClient();

  return {
    mutation: useMutation<TerritoryDataObject, APIError, CreateTerritoryDataObjectProps>({
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries({ queryKey: ['tqModelTree', variables.hierarchyModelId] });
        queryClient.invalidateQueries({
          queryKey: ['hierarchy-models', variables.hierarchyModelId, 'territory-objects'],
        });
        options.onSuccess?.(data, variables, context);
      },
      mutationFn: createTerritoryDataObject(csrfToken),
    }),
  };
};

const useUpdateTerritoryDataObjectMutation = (
  csrfToken: string,
  options: UseMutationOptions<TerritoryDataObject, APIError, UpdateTerritoryDataObjectProps> = {},
) => {
  const queryClient = useQueryClient();

  return {
    mutation: useMutation<TerritoryDataObject, APIError, UpdateTerritoryDataObjectProps>({
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries({ queryKey: ['tqModelTree', variables.hierarchyModelId] });
        queryClient.invalidateQueries({
          queryKey: ['hierarchy-models', variables.hierarchyModelId, 'territory-objects'],
        });
        options.onSuccess?.(data, variables, context);
      },
      mutationFn: updateTerritoryDataObject(csrfToken),
    }),
  };
};

const useDeleteTerritoryDataObjectMutation = (
  csrfToken: string,
  options: UseMutationOptions<null, APIError, DeleteTerritoryDataObjectProps> = {},
) => {
  const queryClient = useQueryClient();

  return {
    mutation: useMutation<null, APIError, DeleteTerritoryDataObjectProps>({
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries({ queryKey: ['tqModelTree', variables.hierarchyModelId] });
        queryClient.invalidateQueries({
          queryKey: ['hierarchy-models', variables.hierarchyModelId, 'territory-objects'],
        });
        options.onSuccess?.(data, variables, context);
      },
      mutationFn: deleteDataObject(csrfToken),
    }),
  };
};

export const TerritoryDataObjectsService = {
  apiUrl,
  getTerritoryDataObjects,
  getTerritoryDataObjectsQueryKey,
  useGetTerritoryDataObjectsQuery,
  getAllViewsTerritoryDataObjects,
  getAllViewsTerritoryDataObjectsQueryKey,
  useGetAllViewsTerritoryDataObjectsQuery,
  useUpdateTerritoryDataObjectMutation,
  useDeleteTerritoryDataObjectMutation,
  useCreateTerritoryDataObjectMutation,
};
